import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SearchableUserSelect from "@/components/SearchableUserSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import QuickReportButton from "@/components/QuickReportButton";
import { toast } from "sonner";
import { Coin, Storefront, ArrowsLeftRight, Receipt, Plus, Trash, Check, ChartLineUp, Trophy, CreditCard, Bank, ArrowDown, ArrowUp, ChartPieSlice, Ranking, Lock, LockOpen } from "@phosphor-icons/react";
import UnlockWallet from "@/components/UnlockWallet";

const fmt = (n) => new Intl.NumberFormat("es-MX").format(Math.round(n || 0));
const stackColor = (i) => ["#CCFF00", "#22C55E", "#3B82F6", "#F59E0B", "#A855F7", "#EC4899", "#EF4444", "#06B6D4"][i % 8];

export default function DydCoin() {
  const { user } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [txs, setTxs] = useState([]);
  const [items, setItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [bets, setBets] = useState([]);
  const [openMatches, setOpenMatches] = useState([]);
  const [tab, setTab] = useState("shop");
  const [newItem, setNewItem] = useState({ name: "", description: "", price_dyd: 100, stock: 10, image_url: "", category: "general" });
  const [showCreate, setShowCreate] = useState(false);
  const [transferForm, setTransferForm] = useState({ to_user_id: "", amount: 50, note: "" });
  const [grantForm, setGrantForm] = useState({ user_id: "", amount: 100, note: "" });
  const [betForm, setBetForm] = useState({ match_id: "", pair_choice: 1, amount: 25 });
  const [exchange, setExchange] = useState(null);
  const [topupForm, setTopupForm] = useState({ package_id: "500", currency: "mxn" });
  const [connectStatus, setConnectStatus] = useState(null);
  const [withdrawForm, setWithdrawForm] = useState({ dyd_amount: 200, currency: "mxn" });
  const [withdrawals, setWithdrawals] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [breakdown, setBreakdown] = useState(null);
  const [ranking, setRanking] = useState([]);
  const [rankingMetric, setRankingMetric] = useState("lifetime_earned");
  const [unlockStatus, setUnlockStatus] = useState(null);
  const [unlockOpen, setUnlockOpen] = useState(false);

  const isAdmin = user?.role === "admin";
  const fireRefresh = () => { try { window.dispatchEvent(new Event("dyd-coin-refresh")); } catch (_) {} };

  const load = async () => {
    try {
      const [w, t, s, b, m, ex, cs, wd, bd, rk, us] = await Promise.all([
        api.get("/dyd-coin/wallet"),
        api.get("/dyd-coin/transactions?limit=100"),
        api.get("/dyd-coin/shop"),
        api.get("/dyd-coin/bets/my").catch(() => ({ data: { bets: [] } })),
        api.get("/matches?limit=200").catch(() => ({ data: [] })),
        api.get("/dyd-coin/exchange").catch(() => ({ data: null })),
        api.get("/dyd-coin/connect/status").catch(() => ({ data: null })),
        api.get("/dyd-coin/withdrawals").catch(() => ({ data: { withdrawals: [] } })),
        api.get("/dyd-coin/breakdown").catch(() => ({ data: null })),
        api.get(`/dyd-coin/ranking?limit=50&metric=${rankingMetric}`).catch(() => ({ data: { ranking: [] } })),
        api.get("/dyd-coin/unlock/status").catch(() => ({ data: null })),
      ]);
      setWallet(w.data);
      setTxs(t.data.transactions || []);
      setItems(s.data.items || []);
      setBets(b.data.bets || []);
      setExchange(ex.data);
      setConnectStatus(cs.data);
      setWithdrawals(wd.data.withdrawals || []);
      setBreakdown(bd.data);
      setRanking(rk.data.ranking || []);
      setUnlockStatus(us.data);
      // Only matches not finalized/validated and where user is NOT a player
      const meId = user?.id;
      const mlist = (Array.isArray(m.data) ? m.data : []).filter((mm) => {
        if (["validado", "finalizado"].includes(mm.status)) return false;
        const ids = [mm?.pair1?.p1_id, mm?.pair1?.p2_id, mm?.pair2?.p1_id, mm?.pair2?.p2_id];
        return !ids.includes(meId);
      }).slice(0, 50);
      setOpenMatches(mlist);
      if (isAdmin) {
        const [u, o] = await Promise.all([
          api.get("/users?limit=1000").catch(() => ({ data: [] })),
          api.get("/admin/dyd-coin/orders").catch(() => ({ data: { orders: [] } })),
        ]);
        setUsers(u.data || []);
        setOrders(o.data.orders || []);
      } else {
        const u = await api.get("/users?limit=200").catch(() => ({ data: [] }));
        setUsers(u.data || []);
      }
      fireRefresh();
    } catch (e) {
      toast.error("Error al cargar DYD Moneda");
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    api.get(`/dyd-coin/ranking?limit=50&metric=${rankingMetric}`)
      .then((r) => setRanking(r.data.ranking || []))
      .catch(() => {});
  }, [rankingMetric]);

  const buy = async (item) => {
    if (!window.confirm(`Confirmar compra: ${item.name} por ${item.price_dyd} DYD?`)) return;
    try {
      const r = await api.post(`/dyd-coin/shop/${item.id}/buy`);
      toast.success(r.data.message);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const createItem = async () => {
    try {
      await api.post("/admin/dyd-coin/shop", newItem);
      toast.success("Producto creado");
      setShowCreate(false);
      setNewItem({ name: "", description: "", price_dyd: 100, stock: 10, image_url: "", category: "general" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm("Eliminar producto?")) return;
    try {
      await api.delete(`/admin/dyd-coin/shop/${id}`);
      toast.success("Eliminado");
      load();
    } catch (e) {
      toast.error("Error");
    }
  };

  const transfer = async () => {
    try {
      const r = await api.post("/dyd-coin/transfer", transferForm);
      toast.success(r.data.message);
      setTransferForm({ to_user_id: "", amount: 50, note: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const grant = async () => {
    try {
      await api.post("/admin/dyd-coin/grant", grantForm);
      toast.success(`Acreditados ${grantForm.amount} DYD`);
      setGrantForm({ user_id: "", amount: 100, note: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const updateOrder = async (id, status) => {
    try {
      await api.put(`/admin/dyd-coin/orders/${id}/status`, { status });
      toast.success("Orden actualizada");
      load();
    } catch (e) {
      toast.error("Error");
    }
  };

  const placeBet = async () => {
    if (!betForm.match_id) { toast.error("Selecciona un partido"); return; }
    try {
      const r = await api.post("/dyd-coin/bets", betForm);
      toast.success(r.data.message);
      setBetForm({ match_id: "", pair_choice: 1, amount: 25 });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al apostar");
    }
  };

  const matchLabel = (m) => {
    const p1 = `${m?.pair1?.p1_name || "?"} / ${m?.pair1?.p2_name || "?"}`;
    const p2 = `${m?.pair2?.p1_name || "?"} / ${m?.pair2?.p2_name || "?"}`;
    return `${p1}  vs  ${p2}`;
  };

  const startTopup = async () => {
    try {
      const r = await api.post("/dyd-coin/topup/checkout", {
        package_id: topupForm.package_id,
        currency: topupForm.currency,
        origin_url: window.location.origin,
      });
      if (r.data.url) {
        window.location.href = r.data.url;
      } else {
        toast.error("Error: sin URL de checkout");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al iniciar recarga");
    }
  };

  const startConnect = async () => {
    try {
      const r = await api.post("/dyd-coin/connect/onboard", {
        country: "MX",
        origin_url: window.location.origin,
      });
      if (r.data.url) window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al iniciar onboarding");
    }
  };

  const submitWithdraw = async () => {
    if (!connectStatus?.payouts_enabled) {
      toast.error("Primero completa el onboarding de Stripe Connect");
      return;
    }
    if (!window.confirm(`¿Retirar ${withdrawForm.dyd_amount} DYD a tu cuenta bancaria?`)) return;
    try {
      const r = await api.post("/dyd-coin/withdraw", withdrawForm);
      toast.success(r.data.message);
      setWithdrawForm({ dyd_amount: 200, currency: "mxn" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al retirar");
    }
  };

  // Manejar retorno de Stripe (recarga)
  useEffect(() => {
    const sid = searchParams.get("topup_session");
    if (sid) {
      (async () => {
        let attempts = 0;
        const poll = async () => {
          try {
            const r = await api.get(`/dyd-coin/topup/status/${sid}`);
            if (r.data.payment_status === "paid") {
              toast.success(`Recarga exitosa: +${r.data.dyd_amount} DYD`);
              searchParams.delete("topup_session");
              setSearchParams(searchParams, { replace: true });
              load();
              return;
            }
            if (r.data.status === "expired") {
              toast.error("La sesión de pago expiró");
              return;
            }
            if (attempts++ < 6) setTimeout(poll, 2000);
          } catch (e) { /* ignore */ }
        };
        poll();
      })();
    } else if (searchParams.get("topup_cancel")) {
      toast.message("Recarga cancelada");
      searchParams.delete("topup_cancel");
      setSearchParams(searchParams, { replace: true });
    } else if (searchParams.get("connect_return")) {
      toast.success("Onboarding completado");
      searchParams.delete("connect_return");
      setSearchParams(searchParams, { replace: true });
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-4" data-testid="dyd-coin-page">
      <div className="flex items-center justify-end">
        <QuickReportButton section="dyd_coin" variant="compact" />
      </div>
      {/* LOCK BANNER */}
      {unlockStatus?.locked && (
        <div className="card-tactical p-4 bg-gradient-to-r from-amber-500/10 to-red-500/10 border-amber-500/40" data-testid="lock-banner">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Lock size={28} weight="fill" className="text-amber-400" />
              <div>
                <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-amber-400">Wallet bloqueada</p>
                <p className="text-sm text-white font-semibold mt-0.5">
                  {unlockStatus.kyc_status === "proof_uploaded"
                    ? "Tu comprobante está en revisión por el admin."
                    : unlockStatus.kyc_status === "rejected"
                      ? `Comprobante rechazado: ${unlockStatus.rejection_reason || "—"}`
                      : `Activa tu wallet recargando $${fmt(unlockStatus.required_mxn)} MXN`}
                </p>
                <p className="text-[10px] text-gray-400 mt-0.5">Mientras tanto, sigues ganando DYD por tu actividad pero no puedes transferir, retirar, apostar ni comprar.</p>
              </div>
            </div>
            <Button onClick={() => setUnlockOpen(true)} className="bg-amber-500 text-black hover:bg-amber-400 font-bold uppercase" data-testid="open-unlock-btn">
              <LockOpen size={14} className="mr-1" /> {unlockStatus.kyc_status === "proof_uploaded" ? "Ver estado" : "Activar wallet"}
            </Button>
          </div>
        </div>
      )}
      {unlockStatus && !unlockStatus.locked && (
        <div className="card-tactical px-3 py-2 bg-green-500/5 border-green-500/20 flex items-center gap-2 text-xs" data-testid="wallet-active-pill">
          <LockOpen size={14} weight="fill" className="text-green-400" />
          <span className="text-green-300 font-semibold">Wallet activa — acceso completo al ecosistema DYD</span>
        </div>
      )}

      <UnlockWallet open={unlockOpen} onClose={() => setUnlockOpen(false)} balance={wallet?.balance} onSuccess={load} />

      {/* HERO Wallet */}
      <div className="card-tactical p-6 bg-gradient-to-br from-[#CCFF00]/10 to-amber-500/5 border-[#CCFF00]/30" data-testid="dyd-wallet-hero">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#CCFF00] flex items-center gap-1">
              <Coin size={12} weight="fill" /> Mi Wallet DYD
            </p>
            <div className="flex items-baseline gap-2 mt-2">
              <span className="font-['Barlow_Condensed'] text-7xl font-black text-[#CCFF00] leading-none" data-testid="dyd-balance">{fmt(wallet?.balance)}</span>
              <span className="text-2xl font-bold text-[#CCFF00]">DYD</span>
            </div>
            <p className="text-xs text-gray-400 mt-1">
              Ganados: <span className="text-green-400 font-bold">{fmt(wallet?.lifetime_earned)}</span> ·
              Gastados: <span className="text-red-400 font-bold">{fmt(wallet?.lifetime_spent)}</span>
            </p>
          </div>
          <div className="grid grid-cols-2 gap-1.5 text-[10px]">
            {wallet?.rules && Object.entries(wallet.rules).map(([k, v]) => (
              <div key={k} className="bg-black/30 px-2 py-1 rounded border border-white/5">
                <span className="text-gray-500 uppercase tracking-wide">{k.replace(/_/g, " ")}</span>
                <span className="ml-1 font-bold text-[#CCFF00]">+{v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#0A0A0A] border border-white/10 flex-wrap">
          <TabsTrigger value="shop" data-testid="tab-shop"><Storefront size={14} className="mr-1" /> Tienda</TabsTrigger>
          <TabsTrigger value="topup" data-testid="tab-topup"><ArrowDown size={14} className="mr-1" /> Recargar</TabsTrigger>
          <TabsTrigger value="withdraw" data-testid="tab-withdraw"><Bank size={14} className="mr-1" /> Retirar</TabsTrigger>
          <TabsTrigger value="bets" data-testid="tab-bets"><ChartLineUp size={14} className="mr-1" /> Apuestas</TabsTrigger>
          <TabsTrigger value="transfer" data-testid="tab-transfer"><ArrowsLeftRight size={14} className="mr-1" /> Transferir</TabsTrigger>
          <TabsTrigger value="breakdown" data-testid="tab-breakdown"><ChartPieSlice size={14} className="mr-1" /> Desglose</TabsTrigger>
          <TabsTrigger value="ranking" data-testid="tab-ranking"><Ranking size={14} className="mr-1" /> Ranking</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history"><Receipt size={14} className="mr-1" /> Historial</TabsTrigger>
          {isAdmin && <TabsTrigger value="admin" data-testid="tab-admin"><Trophy size={14} className="mr-1" /> Admin</TabsTrigger>}
        </TabsList>

        {/* TIENDA */}
        <TabsContent value="shop" className="mt-3">
          {isAdmin && (
            <div className="mb-3">
              <Button onClick={() => setShowCreate(true)} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase text-xs" data-testid="shop-new-item">
                <Plus size={14} className="mr-1" /> Nuevo producto
              </Button>
            </div>
          )}
          {items.length === 0 ? (
            <div className="card-tactical p-8 text-center text-gray-500 text-sm">No hay productos disponibles.</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {items.map((it) => (
                <div key={it.id} className="card-tactical p-3 flex flex-col" data-testid={`shop-item-${it.id}`}>
                  {it.image_url && <img src={it.image_url} alt={it.name} className="w-full h-32 object-cover rounded mb-2 border border-white/5" />}
                  <p className="text-[10px] uppercase tracking-wider text-gray-500">{it.category}</p>
                  <h3 className="font-bold text-white truncate" title={it.name}>{it.name}</h3>
                  <p className="text-[11px] text-gray-400 mt-1 line-clamp-2 flex-1">{it.description}</p>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="font-['Barlow_Condensed'] text-2xl font-black text-[#CCFF00]">{fmt(it.price_dyd)} <span className="text-xs">DYD</span></span>
                    <span className={`text-[10px] uppercase font-bold ${it.stock > 5 ? "text-green-400" : it.stock > 0 ? "text-yellow-400" : "text-red-400"}`}>Stock: {it.stock}</span>
                  </div>
                  <div className="mt-2 flex gap-1">
                    <Button onClick={() => buy(it)} disabled={(wallet?.balance || 0) < it.price_dyd || it.stock <= 0} size="sm" className="flex-1 bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold text-[10px] uppercase" data-testid={`shop-buy-${it.id}`}>
                      Comprar
                    </Button>
                    {isAdmin && (
                      <Button onClick={() => deleteItem(it.id)} size="sm" variant="outline" className="border-red-500/30 text-red-400 h-8 w-8 p-0" data-testid={`shop-delete-${it.id}`}>
                        <Trash size={12} />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* RECARGAR */}
        <TabsContent value="topup" className="mt-3 space-y-3">
          <div className="card-tactical p-4 space-y-3">
            <div className="flex items-center gap-2">
              <ArrowDown size={18} weight="fill" className="text-[#CCFF00]" />
              <h3 className="text-[10px] tracking-widest uppercase font-bold text-[#CCFF00]">Recargar DYD con tarjeta</h3>
              <span className="text-[10px] text-gray-500">1 DYD = ${(exchange?.dyd_to_usd || 0.05).toFixed(2)} USD</span>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
              {(exchange?.packages || []).map((p) => {
                const active = topupForm.package_id === p.id;
                return (
                  <button key={p.id} onClick={() => setTopupForm((f) => ({ ...f, package_id: p.id }))}
                    className={`text-left p-3 rounded border transition-all ${active ? "border-[#CCFF00] bg-[#CCFF00]/10" : "border-white/10 bg-[#0A0A0A] hover:border-white/30"}`}
                    data-testid={`topup-pkg-${p.id}`}>
                    <p className="font-['Barlow_Condensed'] text-3xl font-black text-[#CCFF00] leading-none">{fmt(p.dyd)}</p>
                    <p className="text-[10px] uppercase tracking-widest text-gray-400 mt-0.5">DYD</p>
                    <p className="mt-2 text-xs text-white font-bold">{p.prices[topupForm.currency]} <span className="text-gray-500">{topupForm.currency.toUpperCase()}</span></p>
                  </button>
                );
              })}
            </div>
            <div className="grid grid-cols-2 gap-2 max-w-md">
              <div>
                <Label className="text-[10px] uppercase tracking-widest font-bold text-gray-400">Moneda</Label>
                <Select value={topupForm.currency} onValueChange={(v) => setTopupForm((f) => ({ ...f, currency: v }))}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/15 mt-1" data-testid="topup-currency"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/15">
                    {(exchange?.supported_currencies || ["mxn", "usd", "eur"]).map((c) => (
                      <SelectItem key={c} value={c}>{c.toUpperCase()}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button onClick={startTopup} className="w-full bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="topup-submit">
                  <CreditCard size={14} className="mr-1" /> Pagar con Stripe
                </Button>
              </div>
            </div>
            <p className="text-[10px] text-gray-500">Pago seguro vía Stripe. Tu wallet se acreditará automáticamente al confirmar el pago.</p>
          </div>
        </TabsContent>

        {/* RETIRAR */}
        <TabsContent value="withdraw" className="mt-3 space-y-3">
          <div className="card-tactical p-4 space-y-3">
            <div className="flex items-center gap-2">
              <ArrowUp size={18} weight="fill" className="text-[#CCFF00]" />
              <h3 className="text-[10px] tracking-widest uppercase font-bold text-[#CCFF00]">Retirar DYD a tu cuenta bancaria</h3>
            </div>
            {/* Connect onboarding */}
            {!connectStatus?.connected || !connectStatus?.payouts_enabled ? (
              <div className="border border-yellow-500/30 bg-yellow-500/5 p-3 rounded space-y-2">
                <p className="text-sm text-yellow-200 font-semibold">
                  {!connectStatus?.connected
                    ? "Aún no has vinculado una cuenta bancaria."
                    : connectStatus?.onboarded
                      ? "Tu cuenta está en verificación. Stripe puede pedirte info adicional."
                      : "Onboarding pendiente. Completa los datos en Stripe."}
                </p>
                {connectStatus?.requirements_due?.length > 0 && (
                  <p className="text-[10px] text-yellow-300/70">Requisitos pendientes: {connectStatus.requirements_due.join(", ")}</p>
                )}
                <Button onClick={startConnect} className="bg-[#635BFF] hover:bg-[#7068FF] text-white font-bold text-xs uppercase" data-testid="connect-onboard-btn">
                  <Bank size={14} className="mr-1" /> {connectStatus?.connected ? "Continuar onboarding" : "Vincular cuenta bancaria"}
                </Button>
                <p className="text-[10px] text-gray-500">Powered by Stripe Connect Express · KYC + transferencias internacionales seguras</p>
              </div>
            ) : (
              <div className="border border-green-500/30 bg-green-500/5 p-2 rounded text-xs text-green-300">
                ✓ Cuenta vinculada · {connectStatus.country} · {connectStatus.default_currency?.toUpperCase()} · Retiros habilitados
              </div>
            )}
            <div className="grid grid-cols-3 gap-2 max-w-2xl">
              <div>
                <Label className="text-[10px] uppercase tracking-widest font-bold text-gray-400">DYD a retirar</Label>
                <Input type="number" min={exchange?.min_withdraw_dyd || 200} max={exchange?.max_withdraw_dyd || 100000}
                  value={withdrawForm.dyd_amount}
                  onChange={(e) => setWithdrawForm((f) => ({ ...f, dyd_amount: Math.max(0, Number(e.target.value) || 0) }))}
                  className="bg-[#0A0A0A] border-white/15 mt-1" data-testid="withdraw-amount" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest font-bold text-gray-400">Moneda</Label>
                <Select value={withdrawForm.currency} onValueChange={(v) => setWithdrawForm((f) => ({ ...f, currency: v }))}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/15 mt-1" data-testid="withdraw-currency"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/15">
                    {(exchange?.supported_currencies || ["mxn", "usd", "eur"]).map((c) => (
                      <SelectItem key={c} value={c}>{c.toUpperCase()}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button onClick={submitWithdraw}
                  disabled={!connectStatus?.payouts_enabled || withdrawForm.dyd_amount < (exchange?.min_withdraw_dyd || 200) || (wallet?.balance || 0) < withdrawForm.dyd_amount}
                  className="w-full bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase"
                  data-testid="withdraw-submit"><ArrowUp size={14} className="mr-1" /> Retirar</Button>
              </div>
            </div>
            {/* Quote */}
            {exchange && withdrawForm.dyd_amount > 0 && (() => {
              const fee = Math.round(withdrawForm.dyd_amount * (exchange.withdraw_fee_pct || 0.05));
              const net = withdrawForm.dyd_amount - fee;
              const usd = net * (exchange.dyd_to_usd || 0.05);
              const rate = (exchange.fx_rate || {})[withdrawForm.currency] || 1;
              const fiat = (usd * rate).toFixed(2);
              return (
                <p className="text-[11px] text-gray-400">
                  Comisión {(exchange.withdraw_fee_pct * 100).toFixed(0)}%: <span className="text-red-400 font-bold">-{fee} DYD</span> ·
                  Neto: <span className="text-green-400 font-bold">{net} DYD</span> →
                  <span className="text-[#CCFF00] font-bold ml-1">{fiat} {withdrawForm.currency.toUpperCase()}</span>
                </p>
              );
            })()}
          </div>

          <div className="card-tactical p-0 overflow-hidden">
            <h3 className="text-[10px] tracking-widest uppercase font-bold text-[#CCFF00] p-3">Mis retiros ({withdrawals.length})</h3>
            {withdrawals.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">Sin retiros aún.</div>
            ) : (
              <div className="max-h-[400px] overflow-y-auto divide-y divide-white/5">
                {withdrawals.map((w) => (
                  <div key={w.id} className="px-3 py-2 grid grid-cols-[1fr_120px_120px_120px] gap-2 items-center text-xs" data-testid={`withdraw-${w.id}`}>
                    <div className="min-w-0">
                      <p className="text-white">{w.net_dyd} DYD → {w.fiat_amount} {w.currency.toUpperCase()}</p>
                      <p className="text-[9px] text-gray-500">{new Date(w.created_at).toLocaleString("es-MX")}</p>
                    </div>
                    <span className="font-mono text-[10px] text-gray-400">fee {w.fee_dyd} DYD</span>
                    <span className={`text-[10px] uppercase font-bold ${w.status === "transferred" ? "text-green-400" : w.status === "failed" ? "text-red-400" : "text-yellow-400"}`}>{w.status}</span>
                    <span className="text-right font-mono text-[9px] text-gray-500 truncate" title={w.stripe_transfer_id}>{w.stripe_transfer_id?.slice(0, 12) || "—"}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* APUESTAS */}
        <TabsContent value="bets" className="mt-3 space-y-3">
          <div className="card-tactical p-4 space-y-3 max-w-2xl">
            <div className="flex items-center gap-2">
              <ChartLineUp size={18} weight="fill" className="text-[#CCFF00]" />
              <h3 className="text-[10px] tracking-widest uppercase font-bold text-[#CCFF00]">Apostar en un partido (paga 2x)</h3>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest font-bold text-gray-400">Partido abierto</Label>
              <Select value={betForm.match_id} onValueChange={(v) => setBetForm((f) => ({ ...f, match_id: v }))}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/15 mt-1" data-testid="bet-match-select">
                  <SelectValue placeholder={openMatches.length ? "Seleccionar partido" : "Sin partidos disponibles"} />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/15 max-h-72">
                  {openMatches.map((m) => (
                    <SelectItem key={m.id} value={m.id} className="text-xs">{matchLabel(m)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] uppercase tracking-widest font-bold text-gray-400">A qué pareja</Label>
                <Select value={String(betForm.pair_choice)} onValueChange={(v) => setBetForm((f) => ({ ...f, pair_choice: Number(v) }))}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/15 mt-1" data-testid="bet-pair-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/15">
                    <SelectItem value="1">Pareja 1</SelectItem>
                    <SelectItem value="2">Pareja 2</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest font-bold text-gray-400">Cantidad (DYD)</Label>
                <Input type="number" min="1" max="5000" value={betForm.amount} onChange={(e) => setBetForm((f) => ({ ...f, amount: Math.max(1, Number(e.target.value) || 0) }))} className="bg-[#0A0A0A] border-white/15 mt-1" data-testid="bet-amount" />
              </div>
            </div>
            <Button onClick={placeBet} disabled={!betForm.match_id || betForm.amount <= 0 || (wallet?.balance || 0) < betForm.amount} className="w-full bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="bet-submit">
              <ChartLineUp size={14} className="mr-1" /> Apostar {fmt(betForm.amount)} DYD
            </Button>
            <p className="text-[10px] text-gray-500">Ganas 2× tu apuesta si tu pareja gana. No puedes apostar en tu propio partido.</p>
          </div>

          <div className="card-tactical p-0 overflow-hidden">
            <h3 className="text-[10px] tracking-widest uppercase font-bold text-[#CCFF00] p-3">Mis apuestas ({bets.length})</h3>
            {bets.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">Aún no has apostado.</div>
            ) : (
              <div className="max-h-[400px] overflow-y-auto divide-y divide-white/5">
                {bets.map((b) => (
                  <div key={b.id} className="px-3 py-2 grid grid-cols-[1fr_90px_90px_90px] gap-2 items-center text-xs" data-testid={`bet-${b.id}`}>
                    <div className="min-w-0">
                      <p className="text-white">Partido #{(b.match_id || "").slice(0, 8)} → Pareja {b.pair_choice}</p>
                      <p className="text-[9px] text-gray-500">{new Date(b.created_at).toLocaleString("es-MX")}</p>
                    </div>
                    <span className="font-mono text-[#CCFF00] font-bold">{fmt(b.amount)} DYD</span>
                    <span className={`text-[10px] uppercase font-bold ${b.status === "ganada" ? "text-green-400" : b.status === "perdida" ? "text-red-400" : "text-yellow-400"}`}>{b.status}</span>
                    <span className="text-right font-mono text-green-400">{b.payout ? `+${fmt(b.payout)}` : "—"}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* TRANSFERIR */}
        <TabsContent value="transfer" className="mt-3">
          <div className="card-tactical p-4 space-y-3 max-w-md">
            <div>
              <Label className="text-[10px] uppercase tracking-widest font-bold text-gray-400">Para</Label>
              <SearchableUserSelect
                value={transferForm.to_user_id}
                onChange={(id) => setTransferForm((f) => ({ ...f, to_user_id: id }))}
                options={users.filter((u) => u.id !== user?.id)}
                placeholder="Seleccionar usuario"
                allowClear
                testId="transfer-user-select"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest font-bold text-gray-400">Cantidad (DYD)</Label>
              <Input type="number" min="1" max="10000" value={transferForm.amount} onChange={(e) => setTransferForm((f) => ({ ...f, amount: Math.max(1, Number(e.target.value) || 0) }))} className="bg-[#0A0A0A] border-white/15 mt-1" data-testid="transfer-amount" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest font-bold text-gray-400">Nota (opcional)</Label>
              <Input value={transferForm.note} onChange={(e) => setTransferForm((f) => ({ ...f, note: e.target.value }))} placeholder="Pago de la apuesta..." className="bg-[#0A0A0A] border-white/15 mt-1" />
            </div>
            <Button onClick={transfer} disabled={!transferForm.to_user_id || transferForm.amount <= 0} className="w-full bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="transfer-submit">
              <ArrowsLeftRight size={14} className="mr-1" /> Transferir
            </Button>
          </div>
        </TabsContent>

        {/* DESGLOSE */}
        <TabsContent value="breakdown" className="mt-3 space-y-3">
          <div className="card-tactical p-4 space-y-3">
            <div className="flex items-center gap-2">
              <ChartPieSlice size={18} weight="fill" className="text-[#CCFF00]" />
              <h3 className="text-[10px] tracking-widest uppercase font-bold text-[#CCFF00]">Desglose de DYD ganados por origen</h3>
            </div>
            {!breakdown || breakdown.breakdown?.length === 0 ? (
              <p className="text-sm text-gray-500 py-6 text-center">Aún no tienes ganancias para desglosar.</p>
            ) : (
              <>
                {/* Stacked bar */}
                <div className="flex h-3 rounded overflow-hidden bg-black/40" data-testid="breakdown-bar">
                  {breakdown.breakdown.map((b, i) => (
                    <div key={b.label} style={{ width: `${b.percent}%`, backgroundColor: stackColor(i) }} title={`${b.label}: ${b.percent}%`} />
                  ))}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 mt-2">
                  {breakdown.breakdown.map((b, i) => (
                    <div key={b.label} className="bg-[#0A0A0A] border border-white/10 p-3 rounded" data-testid={`breakdown-${b.label.toLowerCase()}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: stackColor(i) }} />
                        <span className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">{b.label}</span>
                      </div>
                      <p className="font-['Barlow_Condensed'] text-3xl font-black text-[#CCFF00] leading-none">{fmt(b.total)} <span className="text-xs text-white">DYD</span></p>
                      <div className="flex items-baseline justify-between mt-1">
                        <span className="text-[10px] text-gray-500">{b.count} mov.</span>
                        <span className="text-sm font-bold text-white">{b.percent}%</span>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-gray-500 mt-2">
                  Total ganado: <span className="text-white font-bold">{fmt(breakdown.total_earned_grouped)} DYD</span> ·
                  Balance actual: <span className="text-[#CCFF00] font-bold ml-1">{fmt(breakdown.balance)} DYD</span>
                </p>
              </>
            )}
          </div>
        </TabsContent>

        {/* RANKING */}
        <TabsContent value="ranking" className="mt-3 space-y-3">
          <div className="card-tactical p-4">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <div className="flex items-center gap-2">
                <Ranking size={18} weight="fill" className="text-[#CCFF00]" />
                <h3 className="text-[10px] tracking-widest uppercase font-bold text-[#CCFF00]">Ranking DYD Top 50</h3>
              </div>
              <Select value={rankingMetric} onValueChange={setRankingMetric}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/15 w-48" data-testid="ranking-metric"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/15">
                  <SelectItem value="lifetime_earned">Total ganado (histórico)</SelectItem>
                  <SelectItem value="balance">Balance actual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {ranking.length === 0 ? (
              <p className="text-sm text-gray-500 py-6 text-center">Sin datos de ranking aún.</p>
            ) : (
              <div className="overflow-hidden border border-white/5">
                <div className="grid grid-cols-[60px_1fr_120px_120px] gap-2 px-3 py-2 bg-white/5 text-[9px] tracking-[0.2em] uppercase font-bold text-gray-400">
                  <span>#</span><span>Jugador</span><span className="text-right">Ganado</span><span className="text-right">Balance</span>
                </div>
                <div className="divide-y divide-white/5 max-h-[600px] overflow-y-auto">
                  {ranking.map((r) => {
                    const isMe = r.user_id === user?.id;
                    const top3 = r.rank <= 3;
                    const medalColor = r.rank === 1 ? "text-yellow-400" : r.rank === 2 ? "text-gray-300" : r.rank === 3 ? "text-orange-400" : "text-gray-500";
                    return (
                      <div key={r.user_id} className={`grid grid-cols-[60px_1fr_120px_120px] gap-2 px-3 py-2 items-center text-xs ${isMe ? "bg-[#CCFF00]/5" : ""}`} data-testid={`rank-${r.rank}`}>
                        <span className={`font-['Barlow_Condensed'] text-2xl font-black ${medalColor}`}>{top3 ? <Trophy size={20} weight="fill" className="inline mr-1" /> : null}#{r.rank}</span>
                        <div className="min-w-0">
                          <p className={`truncate font-semibold ${isMe ? "text-[#CCFF00]" : "text-white"}`}>{r.name}{isMe && " (tú)"}</p>
                          <p className="text-[9px] text-gray-500 uppercase tracking-wider">{r.role || "—"}{r.category ? ` · Cat ${r.category}` : ""}</p>
                        </div>
                        <span className="text-right font-mono font-bold text-green-400">{fmt(r.lifetime_earned)}</span>
                        <span className="text-right font-mono font-bold text-[#CCFF00]">{fmt(r.balance)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* HISTORIAL */}
        <TabsContent value="history" className="mt-3">
          {txs.length === 0 ? (
            <div className="card-tactical p-8 text-center text-gray-500 text-sm">Sin movimientos aún.</div>
          ) : (
            <div className="card-tactical p-0 overflow-hidden">
              <div className="grid grid-cols-[1fr_120px_120px_100px] gap-2 px-3 py-2 bg-white/5 text-[9px] tracking-[0.2em] uppercase font-bold text-gray-400">
                <span>Descripción</span><span>Tipo</span><span className="text-right">Monto</span><span className="text-right">Saldo</span>
              </div>
              <div className="divide-y divide-white/5 max-h-[500px] overflow-y-auto">
                {txs.map((tx) => (
                  <div key={tx.id} className="grid grid-cols-[1fr_120px_120px_100px] gap-2 px-3 py-2 text-xs items-center" data-testid={`tx-${tx.id}`}>
                    <div className="min-w-0">
                      <p className="text-white truncate" title={tx.description}>{tx.description}</p>
                      <p className="text-[9px] text-gray-500">{new Date(tx.created_at).toLocaleString("es-MX")}</p>
                    </div>
                    <span className="text-[9px] uppercase font-bold text-gray-400">{tx.source}</span>
                    <span className={`text-right font-mono font-bold ${tx.amount > 0 ? "text-green-400" : "text-red-400"}`}>{tx.amount > 0 ? "+" : ""}{fmt(tx.amount)}</span>
                    <span className="text-right font-mono text-gray-500">{fmt(tx.balance_after)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        {/* ADMIN */}
        {isAdmin && (
          <TabsContent value="admin" className="mt-3 space-y-4">
            <div className="card-tactical p-4">
              <h3 className="text-[10px] tracking-widest uppercase font-bold text-[#CCFF00] mb-2">Acreditar/Deducir DYD</h3>
              <div className="grid sm:grid-cols-[1fr_120px_1fr_100px] gap-2">
                <SearchableUserSelect
                  value={grantForm.user_id}
                  onChange={(id) => setGrantForm((f) => ({ ...f, user_id: id }))}
                  options={users}
                  placeholder="Usuario"
                  allowClear
                  testId="grant-user-select"
                />
                <Input type="number" value={grantForm.amount} onChange={(e) => setGrantForm((f) => ({ ...f, amount: Number(e.target.value) || 0 }))} className="bg-[#0A0A0A] border-white/15" placeholder="DYD" data-testid="grant-amount" />
                <Input value={grantForm.note} onChange={(e) => setGrantForm((f) => ({ ...f, note: e.target.value }))} placeholder="Nota (opcional)" className="bg-[#0A0A0A] border-white/15" />
                <Button onClick={grant} disabled={!grantForm.user_id} className="bg-[#CCFF00] text-black font-bold" data-testid="grant-submit">Aplicar</Button>
              </div>
              <p className="text-[10px] text-gray-500 mt-1">Cantidad positiva acredita; negativa deduce.</p>
            </div>

            <div className="card-tactical p-0 overflow-hidden">
              <h3 className="text-[10px] tracking-widest uppercase font-bold text-[#CCFF00] p-3">Órdenes de tienda ({orders.length})</h3>
              {orders.length === 0 ? <div className="p-4 text-center text-gray-500 text-sm">Sin órdenes</div> : (
                <div className="max-h-[400px] overflow-y-auto divide-y divide-white/5">
                  {orders.map((o) => (
                    <div key={o.id} className="px-3 py-2 grid grid-cols-[1fr_1fr_100px_140px] gap-2 items-center text-xs">
                      <span className="text-white truncate">{o.user_name} → {o.item_name}</span>
                      <span className="text-[10px] text-gray-500">{new Date(o.created_at).toLocaleString("es-MX")}</span>
                      <span className={`text-[10px] uppercase font-bold ${o.status === "entregado" ? "text-green-400" : o.status === "cancelado" ? "text-red-400" : "text-yellow-400"}`}>{o.status.replace("_"," ")}</span>
                      <div className="flex gap-1">
                        {o.status === "pendiente_entrega" && (
                          <>
                            <Button onClick={() => updateOrder(o.id, "entregado")} size="sm" className="h-6 text-[9px] bg-green-500/20 text-green-400 hover:bg-green-500/30 px-2"><Check size={10} /></Button>
                            <Button onClick={() => updateOrder(o.id, "cancelado")} size="sm" variant="outline" className="h-6 text-[9px] border-red-500/30 text-red-400 px-2">Cancel</Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        )}
      </Tabs>

      {/* Create item dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-[#0A0A0A] border-white/10" data-testid="create-item-dialog">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] uppercase">Nuevo producto</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Input value={newItem.name} onChange={(e) => setNewItem((n) => ({ ...n, name: e.target.value }))} placeholder="Nombre" className="bg-[#070707] border-white/15" data-testid="new-item-name" />
            <Input value={newItem.description} onChange={(e) => setNewItem((n) => ({ ...n, description: e.target.value }))} placeholder="Descripción" className="bg-[#070707] border-white/15" />
            <div className="grid grid-cols-2 gap-2">
              <Input type="number" value={newItem.price_dyd} onChange={(e) => setNewItem((n) => ({ ...n, price_dyd: Number(e.target.value) || 0 }))} placeholder="Precio DYD" className="bg-[#070707] border-white/15" data-testid="new-item-price" />
              <Input type="number" value={newItem.stock} onChange={(e) => setNewItem((n) => ({ ...n, stock: Number(e.target.value) || 0 }))} placeholder="Stock" className="bg-[#070707] border-white/15" />
            </div>
            <Input value={newItem.image_url} onChange={(e) => setNewItem((n) => ({ ...n, image_url: e.target.value }))} placeholder="URL imagen (opcional)" className="bg-[#070707] border-white/15" />
            <Input value={newItem.category} onChange={(e) => setNewItem((n) => ({ ...n, category: e.target.value }))} placeholder="Categoría" className="bg-[#070707] border-white/15" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)} className="border-white/15">Cancelar</Button>
            <Button onClick={createItem} className="bg-[#CCFF00] text-black font-bold" data-testid="create-item-submit">Crear</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
